import React from "react";
import { useDialogFocus } from "./atoms.jsx";
import { sessionStore, useSession } from "./session.jsx";
import "./session.css";

function SessionPanel({ title, message, action, actionLabel }) {
  return (
    <main className="session-gate" data-testid="session-gate" aria-live="polite">
      <section className="card session-panel">
        <div className="brand-mark" aria-hidden="true">K</div>
        <h1>{title}</h1>
        {message ? <p data-testid="session-message">{message}</p> : null}
        {action ? <button type="button" className="btn primary" onClick={action}>{actionLabel}</button> : null}
      </section>
    </main>
  );
}

function ClosureSupportBanner({ message, onSignIn, onDismiss }) {
  return (
    <aside className="closure-support-banner" data-testid="closure-support-banner" role="status" aria-label="Account closure status">
      <div>
        <strong>Account closure needs confirmation</strong>
        <p>{message}</p>
      </div>
      <div className="closure-support-actions">
        <button type="button" className="btn sm" data-testid="closure-signin" onClick={onSignIn}>Sign in to check</button>
        <button type="button" className="btn sm ghost" data-testid="closure-dismiss" onClick={onDismiss}>Dismiss</button>
      </div>
    </aside>
  );
}

function SignInDialog({ open, onClose, store = sessionStore }) {
  const session = useSession(store);
  const [email, setEmail] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const titleId = "signin-title";
  const detailId = "signin-detail";
  const dialogRef = useDialogFocus({ enabled: open, onClose });
  React.useEffect(() => {
    if (!open) store.cancelMagicLinkRequest();
    return () => store.cancelMagicLinkRequest();
  }, [open, store]);
  if (!open) return null;

  const submit = async (event) => {
    event.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await store.requestMagicLink(email);
    } catch {
      // The store owns the safe, request-id-aware user message.
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="soon-scrim" data-testid="signin-scrim" onMouseDown={() => !sending && onClose?.()} />
      <section
        className="coming-soon modal signin-dialog"
        data-testid="signin-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={detailId}
        tabIndex={-1}
        ref={dialogRef}
      >
        <div className="cs-top">
          <div>
            <h2 id={titleId}>Sign in to KaratScan</h2>
            <p id={detailId}>Enter your email and we will send a one-time sign-in link.</p>
          </div>
          <button type="button" className="btn icon sm ghost" aria-label="Close sign-in" onClick={onClose} disabled={sending}>×</button>
        </div>
        <form className="signin-form" onSubmit={submit}>
          <label className="lbl" htmlFor="signin-email">Email address</label>
          <input
            id="signin-email"
            data-testid="signin-email"
            className="input"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          {session.message ? <p className="session-form-message" data-testid="signin-message" role="status">{session.message}</p> : null}
          <button className="btn primary" data-testid="signin-submit" type="submit" disabled={sending}>
            {sending ? "Sending…" : "Send sign-in link"}
          </button>
        </form>
      </section>
    </>
  );
}

export function SessionBoundary({ children, store = sessionStore }) {
  const session = useSession(store);
  const [signInOpen, setSignInOpen] = React.useState(false);
  React.useEffect(() => { void store.start(); }, [store]);

  if (session.status === "signout_pending") {
    return <SessionPanel title="Finishing sign-out…" message={session.message || "KaratScan is clearing this session before loading anything else."} action={() => store.logout({ automatic: true })} actionLabel="Retry sign-out" />;
  }
  if (session.landing === "consuming") {
    return <SessionPanel title="Signing you in…" message="Verifying your one-time link." />;
  }
  if (session.landing === "storage_required") {
    return <SessionPanel title="Browser storage required" message={session.message} action={() => store.retryConsume()} actionLabel="Retry sign-in" />;
  }
  if (session.landing === "error") {
    return <SessionPanel title="Sign-in link did not work" message={session.message} action={() => { store.dismissLanding(); setSignInOpen(true); }} actionLabel="Request a new link" />;
  }
  if (session.status === "unknown") {
    return <SessionPanel title="Checking your session…" message={session.message} action={session.message ? () => store.revalidate({ explicit: true }) : null} actionLabel="Retry" />;
  }

  return (
    <>
      {children({
        session,
        openSignIn: () => setSignInOpen(true),
        signOut: () => store.logout(),
        updateProfile: (displayName) => store.updateProfile(displayName),
        closeAccount: () => store.closeAccount(),
      })}
      {session.closureUnconfirmed ? (
        <ClosureSupportBanner
          message={session.closureMessage}
          onSignIn={() => setSignInOpen(true)}
          onDismiss={() => store.dismissClosureNotice()}
        />
      ) : null}
      <SignInDialog open={signInOpen} onClose={() => setSignInOpen(false)} store={store} />
      {session.landing === "success" ? (
        <div className="session-toast" data-testid="signin-success" role="status">
          {session.message}
          <button type="button" aria-label="Dismiss" onClick={() => store.dismissLanding()}>×</button>
        </div>
      ) : null}
      {session.notice ? (
        <div className="session-toast" data-testid="session-notice" role="status">
          {session.notice}
          <button type="button" aria-label="Dismiss session notice" onClick={() => store.dismissNotice()}>×</button>
        </div>
      ) : null}
    </>
  );
}

export { ClosureSupportBanner, SignInDialog };
